import { Icon } from 'components/ui';
import React, { useEffect, useState } from 'react';
import request from 'utils/request_ajax';
import DateFormat from 'constants/date_format.js';
import { snakeCase } from 'lodash';
import Tooltip from 'components/shared/Tooltip';
import { TYPES, routes as ROUTES } from 'constants/config';
import { CheckCircle, File, Lock } from 'react-feather';
import { useSelector } from 'react-redux';
import { LabeledCheckbox } from 'components/shared/checkbox';
import StepIndicator from './StepIndicator';
import ActionButton from './ActionButton';
import TaskButton from './TaskButton';
import JourneyEventCard from './JourneyEventCard';

const renderStepIcon = type => {
  switch (type) {
    case TYPES.LESSON:
      return <File className="JourneyContent-icon" size={20} />;
    case TYPES.PATH_TYPE:
      return <Icon className="JourneyContent-icon" name="path" />;
    case TYPES.TASK:
      return <CheckCircle className="JourneyContent-icon" size={20} />;
    default:
      return null;
  }
};

const pathRoute = pathId => {
  return ROUTES.PATH_REPORTING.INDEX(pathId);
};

const lessonRoute = lessonId => {
  return ROUTES.LESSON(lessonId);
};

const AvailabilityIndicator = ({
  locked,
  displayAddedAt,
  updatedAt,
  addedAfterCompletion,
}) => {
  return (
    <div className="JourneyAvailability">
      {locked && (
        <Tooltip
          position="top"
          text={
            displayAddedAt
              ? t('journeys.viewer.added_after')
              : t('journeys.viewer.locked')
          }
        >
          <Lock className="JourneyAvailability-lock" size={20} />
        </Tooltip>
      )}
      {displayAddedAt && (
        <div className="JourneyAvailability-text">
          {`${t('journeys.viewer.added_on')} ${moment(updatedAt).format(
            DateFormat.MONTH_DAY_YEAR_SHORT,
          )}`}
        </div>
      )}
      {addedAfterCompletion && (
        <p className="JourneyAvailability-text">
          {t('journeys.viewer.added_after_completion')}
        </p>
      )}
    </div>
  );
};

const Step = ({ step, index }) => {
  const {
    completedSteps,
    currentStep,
    steps,
    journeyId,
    started,
    startedAt,
    completed,
  } = useSelector(state => state);

  const { previewing } = useSelector(state => state.ui);

  const [submittable, setSubmittable] = useState(false);
  const [showToolTip, setshowToolTip] = useState(false);
  const [loading, setLoading] = useState(true);

  function createMarkup() {
    return { __html: step.description };
  }

  const stepCompleted = !!step.stepProgress && step.stepProgress.completedAt;
  const inLearnerView = completedSteps !== undefined;
  const addedAfterCompletion = step.stepProgress == null && completed;
  const unavailable =
    step.stepId !== currentStep && !stepCompleted && !addedAfterCompletion;
  const unavailableStyles = { opacity: 0.6 };

  const displayAddedAt =
    !completed &&
    startedAt &&
    step.stepUpdatedAt &&
    Date.parse(startedAt) < Date.parse(step.stepCreatedAt);

  const [newAttendance, setNewAttendance] = useState(false);

  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [eventMetaData, setEventMetaData] = useState(null);

  const findRelevantSession = eventSessions => {
    let relevantSession;
    const positiveStatus = eventSessions?.find(session =>
      ['did_attend', 'will_attend'].includes(session.attendance?.status),
    );
    if (selectedSessionId) {
      relevantSession = eventSessions.find(
        session => session.id === selectedSessionId,
      );
    } else if (positiveStatus) {
      relevantSession = positiveStatus;
      setSelectedSessionId(relevantSession.id);
    } else {
      relevantSession =
        eventSessions?.find(session =>
          ['did_not_attend', 'will_not_attend'].includes(
            session.attendance?.status,
          ),
        ) || eventSessions?.[0];
    }
    return relevantSession;
  };

  useEffect(() => {
    if (step.contentType === TYPES.EVENT) {
      request.get(ROUTES.JOURNEY.EVENTS(step.contentId)).then(res => {
        if (res.register) {
          const content = res.register;
          let attendanceData;
          if (content.event.rsvp_type === 'single') {
            const relevantSession = findRelevantSession(content.sessions);
            attendanceData = relevantSession?.attendance || content.attendance;
          } else {
            attendanceData = content.attendance;
          }
          setEventMetaData({
            event: content.event,
            sessions: content.sessions,
            attendance: attendanceData,
            canChangeAttendance: content.meta.policies.can_change_attendance,
            type: content.type,
            userId: content.user_id,
          });
        } else {
          setLoading(false);
        }
      });
    }
    setNewAttendance(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.contentId, newAttendance]);

  useEffect(() => {
    if (eventMetaData) {
      setLoading(false);
      setNewAttendance(false);
    }
  }, [eventMetaData]);

  if (step.contentType === TYPES.EVENT) {
    if (!loading && eventMetaData) {
      return (
        <JourneyEventCard
          step={step}
          index={index}
          previewing={previewing}
          event={eventMetaData.event}
          sessions={eventMetaData.sessions}
          attendance={eventMetaData.attendance}
          canChangeAttendance={eventMetaData.canChangeAttendance}
          type={eventMetaData.type}
          userId={eventMetaData.userId}
          setNewAttendance={setNewAttendance}
          selectedSessionId={selectedSessionId}
          setSelectedSessionId={setSelectedSessionId}
        />
      );
    }
  }

  if (step.contentType === TYPES.TASK) {
    return (
      <div
        className="JourneyContentWrapper"
        data-testid="JourneyContentWrapper"
      >
        <div
          className="JourneyContent"
          style={unavailable && inLearnerView ? unavailableStyles : {}}
        >
          <div className="w-fill">
            <div className="JourneyContent-header">
              <div
                className="JourneyContent-title-container"
                data-testid="JourneyContent-title"
              >
                <div className="JourneyContent-titleSection">
                  {renderStepIcon(step.contentType)}
                  {started === undefined ? (
                    <div className="JourneyContent-taskTitle">{step.title}</div>
                  ) : (
                    <div className="JourneyContent-step">{step.title}</div>
                  )}
                </div>
                {inLearnerView && (
                  <div className="JourneyContent-titleSection JourneyContent-titleSection--right">
                    <AvailabilityIndicator
                      locked={unavailable}
                      displayAddedAt={displayAddedAt}
                      updatedAt={step.stepUpdatedAt}
                      addedAfterCompletion={addedAfterCompletion}
                    />
                  </div>
                )}
              </div>
            </div>
            {(currentStep === step.stepId ||
              (previewing && step.description)) && (
              <div className="JourneyContent-content">
                <div>
                  <div
                    className="JourneyContent-description f-smooth"
                    dangerouslySetInnerHTML={createMarkup()}
                  />
                  {!previewing && (
                    <div className="JourneyContent-button">
                      <LabeledCheckbox
                        className="JourneyContent-checkboxlabel"
                        checkBoxClassName="JourneyContent-checkbox"
                        onChange={e => setSubmittable(e.target.checked)}
                        label={
                          <>
                            <span className="JourneyContent-checkboxtext">
                              {t('journeys.task.check_box')}
                            </span>
                          </>
                        }
                      />
                    </div>
                  )}
                  {showToolTip && (
                    <div className="JourneyContent-tooltip">
                      <Icon className="fz-bigger" name="info" />
                      <div className="JourneyContent-tooltiptext">
                        {t('journeys.task.tool_tip')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {currentStep === step.stepId && (
              <TaskButton
                journeyId={journeyId}
                contentId={step.contentId}
                submittable={submittable}
                showToolTip={setshowToolTip}
                status={step.status}
              />
            )}
          </div>
        </div>
        <StepIndicator
          {...{
            stepNumber: steps.findIndex(x => x.stepId === step.stepId) + 1,
            status: step.status,
            progress: step.stepProgress,
            id: step.stepId,
            stepIndex: index,
          }}
        />
      </div>
    );
  }

  if (step.contentType === TYPES.PATH_TYPE) {
    return (
      <div
        className="JourneyContentWrapper"
        data-testid="JourneyContentWrapper"
      >
        <div
          className="JourneyContent"
          style={unavailable && inLearnerView ? unavailableStyles : {}}
        >
          <div className="w-fill">
            <div className="JourneyContent-header">
              <div
                className="JourneyContent-title-container"
                data-testid="JourneyContent-title"
              >
                <div className="JourneyContent-titleSection">
                  {renderStepIcon(step.contentType)}
                  {started === undefined ? (
                    <div className="JourneyContent-title">
                      <a href={pathRoute(step.contentId)}>{step.title}</a>
                    </div>
                  ) : (
                    <div className="JourneyContent-step">{step.title}</div>
                  )}
                </div>
                {inLearnerView && (
                  <div className="JourneyContent-titleSection JourneyContent-titleSection--right">
                    <AvailabilityIndicator
                      locked={unavailable}
                      displayAddedAt={displayAddedAt}
                      updatedAt={step.stepUpdatedAt}
                      addedAfterCompletion={addedAfterCompletion}
                    />
                  </div>
                )}
              </div>
            </div>
            {!!step.description && (
              <div className="JourneyContent-content">
                <p className="JourneyContent-description f-smooth">
                  {step.description}
                </p>
              </div>
            )}
            <div className="JourneyPath-content">
              {step.contents &&
                step.contents.map(content => {
                  return (
                    <div className="JourneyPath-path-container">
                      <File className="JourneyContent-icon" size={16} />
                      {started === undefined ? (
                        <div
                          key={snakeCase(content.type + content.id)}
                          className="JourneyPath-title"
                        >
                          <a href={lessonRoute(content.id)}>{content.title}</a>
                        </div>
                      ) : (
                        <div
                          key={snakeCase(content.type + content.id)}
                          className="JourneyPath-previewTitle"
                        >
                          <span>{content.title}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            {currentStep === step.stepId && inLearnerView && (
              <ActionButton
                type={step.type}
                urls={step.urls}
                started={started}
              />
            )}
          </div>
        </div>
        <StepIndicator
          {...{
            stepNumber: steps.findIndex(x => x.stepId === step.stepId) + 1,
            status: step.status,
            progress: step.stepProgress,
            id: step.stepId,
            stepIndex: index,
          }}
        />
      </div>
    );
  }

  return (
    <div className="JourneyContentWrapper" data-testid="JourneyContentWrapper">
      <div
        className="JourneyContent"
        style={unavailable && inLearnerView ? unavailableStyles : {}}
      >
        <div className="w-fill">
          <div className="JourneyContent-header">
            <div
              className="JourneyContent-title-container"
              data-testid="JourneyContent-title"
            >
              <div className="JourneyContent-titleSection">
                {renderStepIcon(step.contentType)}
                {started === undefined ? (
                  <div className="JourneyContent-title">
                    <a href={lessonRoute(step.contentId)}>{step.title}</a>
                  </div>
                ) : (
                  <div className="JourneyContent-step">{step.title}</div>
                )}
              </div>
              {inLearnerView && (
                <div className="JourneyContent-titleSection JourneyContent-titleSection--right">
                  <AvailabilityIndicator
                    locked={unavailable}
                    displayAddedAt={displayAddedAt}
                    updatedAt={step.stepUpdatedAt}
                    addedAfterCompletion={addedAfterCompletion}
                  />
                </div>
              )}
            </div>
          </div>
          {!!step.description && (
            <div className="JourneyContent-content">
              <p className="JourneyContent-description f-smooth">
                {step.description}
              </p>
            </div>
          )}
          {currentStep === step.stepId && (
            <ActionButton type={step.type} urls={step.urls} started={started} />
          )}
        </div>
      </div>
      <StepIndicator
        {...{
          stepNumber: steps.findIndex(x => x.stepId === step.stepId) + 1,
          status: step.status,
          progress: step.stepProgress,
          id: step.stepId,
        }}
      />
    </div>
  );
};

export default Step;
